import { CalendarIcon } from '@navikt/aksel-icons';
import { ContextDataType, useContextGetData, useContextSaveData } from 'appData/PlanleggerDataContext';
import { PlanleggerRoutes } from 'appData/routes';
import { usePlanleggerNavigator } from 'appData/usePlanleggerNavigator';
import { useStepData } from 'appData/useStepData';
import { BlueRadioGroup } from 'components/form-wrappers/BlueRadioGroup';
import { PlanleggerStepPage } from 'components/page/PlanleggerStepPage';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Arbeidsstatus } from 'types/Arbeidssituasjon';
import { HvemPlanleggerType } from 'types/HvemPlanlegger';
import { erAlenesøker as erAlene, getTekstForDeSomHarRett } from 'utils/HvemPlanleggerUtils';
import { erBarnetAdoptert } from 'utils/barnetUtils';
import { utledHvemSomHarRett } from 'utils/hvemHarRettUtils';
import { finnAntallUkerOgDagerMedForeldrepenger, finnUttaksdata } from 'utils/uttakUtils';

import { BodyShort, Heading, Link, Radio, ReadMore, Spacer, VStack } from '@navikt/ds-react';

import { links } from '@navikt/fp-constants';
import { RhfForm, StepButtonsHookForm } from '@navikt/fp-form-hooks';
import { HvorLangPeriodePlanlegger, KontoBeregningDto } from '@navikt/fp-types';
import { DekningsgradUtbetalingEksempel, Infobox } from '@navikt/fp-ui';
import { useScrollBehaviour } from '@navikt/fp-utils';
import { isRequired, notEmpty } from '@navikt/fp-validation';

import { NårBareEnPartHarRettInfoboks } from './infoboks/NårBareEnPartHarRettInfoboks';
import { ValgtDekningsgradInfoboks } from './infoboks/ValgtDekningsgradInfoboks';

interface Props {
    stønadskvoter: { '100': KontoBeregningDto; '80': KontoBeregningDto };
}

export const HvorLangPeriodeSteg = ({ stønadskvoter }: Props) => {
    const intl = useIntl();
    const navigator = usePlanleggerNavigator();
    const stepConfig = useStepData();

    const periode = useContextGetData(ContextDataType.HVOR_LANG_PERIODE);
    const hvemPlanlegger = notEmpty(useContextGetData(ContextDataType.HVEM_PLANLEGGER));
    const barnet = notEmpty(useContextGetData(ContextDataType.OM_BARNET));
    const arbeidssituasjon = notEmpty(useContextGetData(ContextDataType.ARBEIDSSITUASJON));

    const oppdaterPeriode = useContextSaveData(ContextDataType.HVOR_LANG_PERIODE);
    const oppdaterFordeling = useContextSaveData(ContextDataType.FORDELING);
    const oppdaterUttaksplan = useContextSaveData(ContextDataType.UTTAKSPLAN);

    const formMethods = useForm<HvorLangPeriodePlanlegger>({ defaultValues: periode });

    const erAlenesøker = erAlene(hvemPlanlegger);
    const erAdopsjon = erBarnetAdoptert(barnet);

    const onSubmit = (formValues: HvorLangPeriodePlanlegger) => {
        oppdaterPeriode(formValues);

        if (periode && periode.dekningsgrad !== formValues.dekningsgrad) {
            // Dersom dekningsgrad endres, og den er tidligere valgt, må vi tilbakestille fordeling og uttaksplan.
            // Bruker kan ha gjort endringer i fordeling og uttaksplan basert på en annen dekningsgrad som ikke lenger stemmer.
            oppdaterFordeling(undefined);
            oppdaterUttaksplan(undefined);
        }

        const erFarOgFar = hvemPlanlegger.type === HvemPlanleggerType.FAR_OG_FAR;
        const beggeHarRett = arbeidssituasjon.status === Arbeidsstatus.JOBBER && !!arbeidssituasjon.jobberAnnenPart;
        const nextRoute =
            beggeHarRett && !(erFarOgFar && barnet.erFødsel)
                ? PlanleggerRoutes.FORDELING
                : PlanleggerRoutes.PLANEN_DERES;

        if (nextRoute === PlanleggerRoutes.PLANEN_DERES) {
            oppdaterFordeling(undefined);
        }

        return navigator.goToNextStep(nextRoute);
    };

    const dekningsgrad = formMethods.watch('dekningsgrad');
    const valgtDekningsgrad = dekningsgrad ?? periode?.dekningsgrad;

    const hvemHarRett = utledHvemSomHarRett(arbeidssituasjon);

    const deSomHarRett = getTekstForDeSomHarRett(hvemPlanlegger, hvemHarRett, intl);

    const stønadskvote100 = stønadskvoter['100'];
    const stønadskvote80 = stønadskvoter['80'];

    const stønadskvote = valgtDekningsgrad === '100' ? stønadskvote100 : stønadskvote80;
    const valgtStønadskvote = valgtDekningsgrad ? stønadskvote : undefined;

    const uttaksdata100 = finnUttaksdata(hvemHarRett, hvemPlanlegger, stønadskvote100, barnet);
    const uttaksdata80 = finnUttaksdata(hvemHarRett, hvemPlanlegger, stønadskvote80, barnet);

    const antallUkerOgDager100 = finnAntallUkerOgDagerMedForeldrepenger(stønadskvote100);
    const antallUkerOgDager80 = finnAntallUkerOgDagerMedForeldrepenger(stønadskvote80);
    const antallUkerOgDager = valgtDekningsgrad === '100' ? antallUkerOgDager100 : antallUkerOgDager80;

    const kunEnAvSøkereneHarRett = hvemHarRett === 'kunSøker1HarRett' || hvemHarRett === 'kunSøker2HarRett';

    const { ref, scrollToBottom } = useScrollBehaviour();

    return (
        <PlanleggerStepPage ref={ref} steps={stepConfig} goToStep={navigator.goToNextStep}>
            <RhfForm formMethods={formMethods} onSubmit={onSubmit} shouldUseFlexbox>
                <VStack gap="space-40" style={{ flex: 1 }}>
                    <VStack gap="space-32">
                        <Heading size="medium" spacing level="2">
                            <FormattedMessage id="HvorLangPeriodeSteg.Tittel" />
                        </Heading>
                        <Infobox
                            header={<FormattedMessage id="HvorLangPeriodeSteg.Infoboks.HvorLangPeriodeTittel" />}
                            icon={
                                <CalendarIcon
                                    height={24}
                                    width={24}
                                    color="var(--ax-bg-neutral-strong)"
                                    fontSize="1.5rem"
                                    aria-hidden
                                />
                            }
                            color="gray"
                        >
                            <BodyShort>
                                <FormattedMessage
                                    id="HvorLangPeriodeSteg.Infoboks.HvorLangPeriodeTekst"
                                    values={{
                                        beggeHarRett: hvemHarRett === 'beggeHarRett',
                                        uker100: antallUkerOgDager100.uker,
                                        dager100: antallUkerOgDager100.dager,
                                        uker80: antallUkerOgDager80.uker,
                                        dager80: antallUkerOgDager80.dager,
                                    }}
                                />
                            </BodyShort>
                        </Infobox>
                        {!erAlenesøker &&
                            kunEnAvSøkereneHarRett &&
                            hvemPlanlegger.type !== HvemPlanleggerType.FAR_OG_FAR && (
                                <NårBareEnPartHarRettInfoboks
                                    hvemPlanlegger={hvemPlanlegger}
                                    arbeidssituasjon={arbeidssituasjon}
                                />
                            )}
                        {kunEnAvSøkereneHarRett &&
                            hvemPlanlegger.type === HvemPlanleggerType.FAR_OG_FAR &&
                            erAdopsjon && (
                                <Infobox
                                    header={
                                        <FormattedMessage id="HvorLangPeriodeSteg.Infoboks.KunEnAvFedreneHarRett" />
                                    }
                                    color="gray"
                                >
                                    <VStack gap="space-8">
                                        <BodyShort>
                                            <FormattedMessage id="HvorLangPeriodeSteg.Infoboks.NårBareEnHarRett" />
                                        </BodyShort>
                                        <BodyShort>
                                            <FormattedMessage
                                                id="HvorLangPeriodeSteg.Infoboks.ManFårEnDel"
                                                values={{
                                                    a: (msg) => (
                                                        // To fedre har ikke mor i søknaden, så lenken skal peke til
                                                        // "Far og far har rett"-seksjonen, ikke aktivitetskrav-til-mor-ankeret.
                                                        <Link
                                                            inlineText
                                                            href={links.foreldrepengerFarOgFar}
                                                            rel="noreferrer"
                                                            target="_blank"
                                                        >
                                                            {msg}
                                                        </Link>
                                                    ),
                                                }}
                                            />
                                        </BodyShort>
                                    </VStack>
                                </Infobox>
                            )}
                        <ReadMore header={<FormattedMessage id="HvorLangPeriodeSteg.ReadMore.Kroner" />}>
                            <DekningsgradUtbetalingEksempel />
                        </ReadMore>
                        <BlueRadioGroup
                            name="dekningsgrad"
                            control={formMethods.control}
                            label={
                                <FormattedMessage id="HvorLangPeriodeSteg.HvorLangPeriode" values={{ deSomHarRett }} />
                            }
                            validate={[
                                isRequired(
                                    intl.formatMessage(
                                        {
                                            id: 'HvorLangPeriodeSteg.HvorLangPeriode.Required',
                                        },
                                        { deSomHarRett },
                                    ),
                                ),
                            ]}
                            onChange={scrollToBottom}
                        >
                            <Radio value={'100'} autoFocus>
                                <FormattedMessage
                                    id="HvorLangPeriodeSteg.100"
                                    values={{
                                        uker100: antallUkerOgDager100.uker,
                                        dager100: antallUkerOgDager100.dager,
                                    }}
                                />
                            </Radio>
                            <Radio value={'80'}>
                                <FormattedMessage
                                    id="HvorLangPeriodeSteg.80"
                                    values={{ uker80: antallUkerOgDager80.uker, dager80: antallUkerOgDager80.dager }}
                                />
                            </Radio>
                        </BlueRadioGroup>
                        {valgtStønadskvote && (
                            <VStack gap="space-8">
                                <ValgtDekningsgradInfoboks
                                    key={valgtDekningsgrad}
                                    barnet={barnet}
                                    hvemPlanlegger={hvemPlanlegger}
                                    arbeidssituasjon={arbeidssituasjon}
                                    valgtStønadskvote={valgtStønadskvote}
                                    uttaksdata100={uttaksdata100}
                                    uttaksdata80={uttaksdata80}
                                    valgtDekningsgrad={valgtDekningsgrad}
                                    antallUkerOgDager={antallUkerOgDager}
                                />
                            </VStack>
                        )}
                    </VStack>
                    <Spacer />
                    <StepButtonsHookForm<HvorLangPeriodePlanlegger>
                        saveDataOnPreviousClick={oppdaterPeriode}
                        goToPreviousStep={navigator.goToPreviousDefaultStep}
                        useSimplifiedTexts
                    />
                </VStack>
            </RhfForm>
        </PlanleggerStepPage>
    );
};
